import { useEffect, useState, useCallback, useRef } from 'react';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { TeamColor } from '../types/match';

interface UseNativeSpeechProps {
  handleCommand: (command: string[]) => void;
  isController: boolean;
  team1Color: TeamColor;
  team2Color: TeamColor;
}

/**
 * Native iOS Speech Recognition Hook
 * Uses Apple's Speech.framework via Capacitor plugin
 * This enables external microphone support on iOS
 */
export function useNativeSpeechRecognition({
  handleCommand,
  isController,
  team1Color,
  team2Color
}: UseNativeSpeechProps) {
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  
  const isInCooldown = useRef(false);
  const lastCommandTime = useRef(0);
  const COOLDOWN_MS = 1500; // Cooldown between commands

  const startCooldown = useCallback(() => {
    isInCooldown.current = true;
    setTimeout(() => {
      isInCooldown.current = false;
    }, COOLDOWN_MS);
  }, []);

  const processVoiceCommand = useCallback((transcriptText: string) => {
    if (isInCooldown.current) {
      return;
    }

    const transcript = transcriptText.toLowerCase();
    console.log('Processing transcript:', transcript);

    // Phonetic alphabet variants
    const colorPhonetics: Record<string, string> = {
      'red': 'romeo',
      'blue': 'bravo',
      'green': 'golf',
      'yellow': 'yankee',
      'orange': 'oscar',
      'purple': 'charlie'
    };

    const team1Phonetic = colorPhonetics[team1Color] || '';
    const team2Phonetic = colorPhonetics[team2Color] || '';

    // Team 1 Color Variants
    if (
      transcript.includes(team1Color) ||
      transcript.includes(team1Phonetic) ||
      // Yellow variants
      (team1Color === 'yellow' && (
        transcript.includes('hello') ||
        transcript.includes('yankee')
      )) ||
      // Orange variants
      (team1Color === 'orange' && (
        transcript.includes('oscar') ||
        transcript.includes('range') ||
        transcript.includes('armed') ||
        transcript.includes('or end') ||
        transcript.includes('are in')
      )) ||
      // Purple variants
      (team1Color === 'purple' && (
        transcript.includes('triple') ||
        transcript.includes('charlie')
      )) ||
      // Green variants
      (team1Color === 'green' && (
        transcript.includes('cream') ||
        transcript.includes('seen') ||
        transcript.includes('seem') ||
        transcript.includes('golf') ||
        transcript.includes('screen')
      )) ||
      // Blue variants
      (team1Color === 'blue' && (
        transcript.includes('bravo') ||
        transcript.includes('view') ||
        transcript.includes('flew') ||
        transcript.includes('flu') ||
        transcript.includes('through') ||
        transcript.includes('true') ||
        transcript.includes('lose') ||
        transcript.includes('brother') ||
        transcript.includes('play') ||
        transcript.includes('new') ||
        transcript.includes('three') ||
        transcript.includes('3.')
      )) ||
      // Red variants
      (team1Color === 'red' && (
        transcript.includes('romeo') ||
        transcript.includes('read') ||
        transcript.includes('tread') ||
        transcript.includes('trade') ||
        transcript.includes('rex') ||
        transcript.includes('head') ||
        transcript.includes('great')
      ))
    ) {
      console.log(`Team 1 (${team1Color}) point detected`);
      handleCommand([`${team1Color} point`]);
      setTranscript('');
      startCooldown();
      return;
    }

    // Team 2 Color Variants
    if (
      transcript.includes(team2Color) ||
      transcript.includes(team2Phonetic) ||
      // Yellow variants
      (team2Color === 'yellow' && (
        transcript.includes('hello') ||
        transcript.includes('yankee')
      )) ||
      // Orange variants
      (team2Color === 'orange' && (
        transcript.includes('oscar') ||
        transcript.includes('range') ||
        transcript.includes('armed') ||
        transcript.includes('or end') ||
        transcript.includes('are in')
      )) ||
      // Purple variants
      (team2Color === 'purple' && (
        transcript.includes('triple') ||
        transcript.includes('charlie')
      )) ||
      // Green variants
      (team2Color === 'green' && (
        transcript.includes('cream') ||
        transcript.includes('seen') ||
        transcript.includes('seem') ||
        transcript.includes('golf') ||
        transcript.includes('screen')
      )) ||
      // Blue variants
      (team2Color === 'blue' && (
        transcript.includes('bravo') ||
        transcript.includes('view') ||
        transcript.includes('flew') ||
        transcript.includes('flu') ||
        transcript.includes('through') ||
        transcript.includes('true') ||
        transcript.includes('lose') ||
        transcript.includes('brother') ||
        transcript.includes('play') ||
        transcript.includes('new')
      )) ||
      // Red variants
      (team2Color === 'red' && (
        transcript.includes('romeo') ||
        transcript.includes('read') ||
        transcript.includes('tread') ||
        transcript.includes('trade') ||
        transcript.includes('rex') ||
        transcript.includes('head') ||
        transcript.includes('great')
      ))
    ) {
      console.log(`Team 2 (${team2Color}) point detected`);
      handleCommand([`${team2Color} point`]);
      setTranscript('');
      startCooldown();
      return;
    }

    // Reverse point
    if (
      transcript === 'reverse' ||
      transcript.includes('reverse point') ||
      transcript.includes('reversepoint') ||
      transcript.includes('reverse a point') ||
      transcript.includes('reverse one point') ||
      transcript.includes('reverse the point') ||
      transcript.includes('verse point') ||
      transcript.includes('versepoint')
    ) {
      console.log('Reverse point detected');
      handleCommand(['reverse point']);
      setTranscript('');
      startCooldown();
      return;
    }

    // Reset match
    if (
      transcript.includes('reset match') ||
      transcript.includes('resetmatch') ||
      transcript.includes('reset mach') ||
      transcript.includes('reset the match')
    ) {
      console.log('Reset match detected');
      handleCommand(['reset match']);
      setTranscript('');
      startCooldown();
      return;
    }

    // Confirm (for reset confirmation)
    if (
      transcript.includes('confirm') ||
      transcript.includes('confirmed') ||
      transcript.includes('confirm reset')
    ) {
      console.log('Confirm detected');
      handleCommand(['confirm']);
      setTranscript('');
      startCooldown();
      return;
    }

    // Cancel
    if (transcript.includes('cancel') || transcript.includes('cancel set')) {
      console.log('Cancel detected');
      handleCommand(['cancel']);
      setTranscript('');
      startCooldown();
      return;
    }
  }, [team1Color, team2Color, handleCommand, startCooldown]);

  useEffect(() => {
    if (!isController) {
      return;
    }

    let isActive = true;
    let partialListener: any = null;

    const initSpeech = async () => {
      try {
        // Check if speech recognition is available
        const { available } = await SpeechRecognition.available();
        if (!available) {
          setError('Speech recognition not available on this device');
          return;
        }

        // Request permissions
        const { speechRecognition } = await SpeechRecognition.requestPermissions();
        if (speechRecognition !== 'granted') {
          setError('Microphone permission denied. Please enable in Settings.');
          return;
        }

        console.log('Starting native speech recognition...');

        // Listen for partial results
        partialListener = await SpeechRecognition.addListener(
          'partialResults',
          (data: any) => {
            if (!isActive) return;
            
            const transcriptText = data.matches?.[0] || '';
            if (transcriptText) {
              setTranscript(transcriptText);
              processVoiceCommand(transcriptText);
            }
          }
        );

        // Start listening
        await SpeechRecognition.start({
          language: 'en-US',
          maxResults: 1,
          partialResults: true,
          popup: false,
        });

        setIsListening(true);
        setError(null);
        console.log('Native speech recognition started successfully');

      } catch (err: any) {
        console.error('Speech recognition error:', err);
        setError(err.message || 'Failed to start speech recognition');
        setIsListening(false);
      }
    };

    initSpeech();

    return () => {
      isActive = false;
      
      if (partialListener) {
        partialListener.remove();
      }
      
      SpeechRecognition.stop().catch((err) => {
        console.error('Error stopping speech recognition:', err);
      });
      
      setIsListening(false);
    };
  }, [isController, processVoiceCommand]);

  return { transcript, error, isListening, hasNetworkError: false };
}

